import { useState, useEffect, useCallback } from 'react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-toastify';
import { FiDownload, FiPlus, FiCheck, FiX, FiClock, FiCoffee, FiRefreshCw, FiZap, FiTrash2 } from 'react-icons/fi';

const STATUS_COLORS = { Present: '#10b981', Absent: '#ef4444', 'Half Day': '#f59e0b', Late: '#f97316' };
const APPROVAL_COLORS = { Pending: '#f59e0b', Approved: '#10b981', Rejected: '#ef4444' };
const REQUIRED_MINUTES = 480; // 8 hours

const fmtDur = (mins) => {
  if (!mins || mins <= 0) return '0h 0m';
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return `${h}h ${m}m`;
};

const toMinutes = (time) => {
  if (!time) return 0;
  const [h, m] = time.split(':').map(Number);
  return h * 60 + m;
};

const AttendancePage = () => {
  const { user } = useAuth();
  const isAdminHR = user.role === 'admin' || user.role === 'hr';
  const [attendance, setAttendance] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const todayStr = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);
  const [filterApproval, setFilterApproval] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [showMark, setShowMark] = useState(false);
  const [markForm, setMarkForm] = useState({ employee: '', date: '', status: 'Present', checkIn: '09:00', checkOut: '18:00' });
  const [selectedIds, setSelectedIds] = useState([]);

  const [extraModal, setExtraModal] = useState(null); // { id, worked }
  const [extraForm, setExtraForm] = useState({ hours: '', minutes: '', note: '' });

  const [todayStatus, setTodayStatus] = useState(null);
  const [checkingIn, setCheckingIn] = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [lunchLoading, setLunchLoading] = useState(false);

  const fetchAttendance = useCallback(async () => {
    try {
      setLoading(true);
      const params = startDate && endDate
        ? { startDate, endDate }
        : { month, year };
      if (filterApproval) params.approval = filterApproval;
      if (isAdminHR && filterEmployee) params.employee = filterEmployee;
      const res = await api.get('/attendance', { params });
      setAttendance(res.data);
    } catch (err) { toast.error('Failed to fetch attendance'); }
    finally { setLoading(false); }
  }, [month, year, startDate, endDate, filterApproval, filterEmployee, isAdminHR]);

  useEffect(() => { fetchAttendance(); }, [fetchAttendance]);

  const fetchToday = useCallback(async () => {
    try {
      const res = await api.get('/attendance/today');
      setTodayStatus(res.data);
    } catch (err) { /* ignore */ }
  }, []);

  useEffect(() => { fetchToday(); }, [fetchToday]);

  useEffect(() => {
    if (isAdminHR) {
      api.get('/employees', { params: { limit: 200 } }).then(res => setEmployees(res.data.employees || [])).catch(() => {});
    }
  }, [isAdminHR]);

  const handleCheckIn = async () => {
    try {
      setCheckingIn(true);
      let payload = {};
      if (navigator.geolocation) {
        await new Promise((resolve) => {
          navigator.geolocation.getCurrentPosition(
            (pos) => { payload.lat = pos.coords.latitude; payload.lng = pos.coords.longitude; resolve(); },
            () => resolve(),
            { timeout: 5000 }
          );
        });
      }
      const res = await api.post('/attendance/check-in', payload);
      setTodayStatus(res.data);
      toast.success('Checked in successfully!');
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Check-in failed');
    } finally { setCheckingIn(false); }
  };

  const handleCheckOut = async () => {
    try {
      setCheckingOut(true);
      const res = await api.put('/attendance/check-out');
      setTodayStatus(res.data);
      toast.success('Checked out successfully!');
      fetchAttendance();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Check-out failed');
    } finally { setCheckingOut(false); }
  };

  const handleLunchStart = async () => {
    try {
      setLunchLoading(true);
      const res = await api.put('/attendance/lunch-start');
      setTodayStatus(res.data);
      toast.success('Lunch break started');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setLunchLoading(false); }
  };

  const handleLunchEnd = async () => {
    try {
      setLunchLoading(true);
      const res = await api.put('/attendance/lunch-end');
      setTodayStatus(res.data);
      toast.success('Lunch break ended');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed');
    } finally { setLunchLoading(false); }
  };

  const handleMark = async (e) => {
    e.preventDefault();
    try {
      await api.post('/attendance', markForm);
      toast.success('Attendance marked');
      setShowMark(false);
      fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to mark attendance'); }
  };

  const handleMarkPresent = async (id) => {
    try {
      await api.put(`/attendance/${id}/mark-present`);
      toast.success('Marked as Present');
      fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to mark present'); }
  };

  const handleApproval = async (id, approval) => {
    try {
      await api.put(`/attendance/${id}/approve`, { approval });
      toast.success(`Attendance ${approval.toLowerCase()}`);
      fetchAttendance();
    } catch (err) { toast.error('Approval failed'); }
  };

  const handleRequestExtra = async (e) => {
    e.preventDefault();
    const totalMins = (parseInt(extraForm.hours || 0) * 60) + parseInt(extraForm.minutes || 0);
    if (totalMins <= 0) return toast.error('Enter valid extra hours');
    try {
      await api.put(`/attendance/${extraModal.id}/request-extra`, { extraHours: totalMins, extraHoursNote: extraForm.note });
      toast.success('Extra hours request submitted');
      setExtraModal(null);
      setExtraForm({ hours: '', minutes: '', note: '' });
      fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit request'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this attendance record? This cannot be undone.')) return;
    try {
      await api.delete(`/attendance/${id}`);
      toast.success('Attendance record deleted');
      fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Delete failed'); }
  };

  const handleExtraAction = async (id, action) => {
    try {
      await api.put(`/attendance/${id}/${action}-extra`);
      toast.success(`Extra hours ${action}d`);
      fetchAttendance();
    } catch (err) { toast.error(`Failed to ${action} extra hours`); }
  };

  const handleBulkApproval = async (approval) => {
    if (selectedIds.length === 0) return toast.error('Select records first');
    try {
      await api.put('/attendance/bulk-approve', { ids: selectedIds, approval });
      toast.success(`${selectedIds.length} records ${approval.toLowerCase()}`);
      setSelectedIds([]);
      fetchAttendance();
    } catch (err) { toast.error('Bulk approval failed'); }
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
  };

  const toggleSelectAll = () => {
    const pendingIds = attendance.filter(a => a.approval === 'Pending').map(a => a._id);
    setSelectedIds(prev => prev.length === pendingIds.length ? [] : pendingIds);
  };

  const handleExport = async () => {
    try {
      const startDate = `${year}-${String(month).padStart(2, '0')}-01`;
      const endDate = `${year}-${String(month).padStart(2, '0')}-${new Date(year, month, 0).getDate()}`;
      const res = await api.get('/attendance/export', { params: { startDate, endDate }, responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `attendance-${month}-${year}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) { toast.error('Export failed'); }
  };

  const now = new Date();
  const pendingCount = attendance.filter(a => a.approval === 'Pending').length;

  // Calculate live worked time for today panel
  const getLiveWorked = () => {
    if (!todayStatus || !todayStatus.checkIn) return 0;
    if (todayStatus.checkOut) return todayStatus.totalWorked || 0;
    const nowMins = now.getHours() * 60 + now.getMinutes();
    const inMins = toMinutes(todayStatus.checkIn);
    const lunch = todayStatus.lunchDuration || (todayStatus.lunchStart && todayStatus.lunchEnd
      ? toMinutes(todayStatus.lunchEnd) - toMinutes(todayStatus.lunchStart)
      : (todayStatus.lunchStart && !todayStatus.lunchEnd ? nowMins - toMinutes(todayStatus.lunchStart) : 0));
    return Math.max(0, nowMins - inMins - lunch);
  };

  const liveWorked = getLiveWorked();
  const progressPct = Math.min(100, Math.round((liveWorked / REQUIRED_MINUTES) * 100));
  const isOnLunch = todayStatus && todayStatus.lunchStart && !todayStatus.lunchEnd;

  return (
    <div>
      <div className="page-header">
        <h1><FiClock style={{ marginRight: '0.5rem' }} /> Attendance</h1>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn-secondary" onClick={fetchAttendance} title="Refresh"><FiRefreshCw /></button>
          {isAdminHR && <button className="btn-primary" onClick={() => setShowMark(!showMark)}><FiPlus /> Mark</button>}
          {isAdminHR && <button className="btn-secondary" onClick={handleExport}><FiDownload /> Export</button>}
        </div>
      </div>

      {/* Check-in Panel — employees and HR */}
      {user.role !== 'admin' && <div className="checkin-panel">
        <div className="checkin-panel-left">
          <div className="checkin-date">
            <h3>{now.toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</h3>
            <p className="checkin-time">{now.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</p>
          </div>
        </div>

        <div className="checkin-panel-center">
          {todayStatus ? (
            <div className="checkin-status-info">
              <div className="checkin-times">
                <div className="checkin-time-block">
                  <span className="checkin-time-label">Check In</span>
                  <span className="checkin-time-value">{todayStatus.checkIn || '--:--'}</span>
                </div>
                {todayStatus.lunchStart && (
                  <>
                    <div className="checkin-time-block lunch-block">
                      <span className="checkin-time-label">Lunch</span>
                      <span className="checkin-time-value lunch-time">
                        {todayStatus.lunchStart}{todayStatus.lunchEnd ? ` - ${todayStatus.lunchEnd}` : ' ...'}
                      </span>
                    </div>
                  </>
                )}
                <div className="checkin-time-block">
                  <span className="checkin-time-label">Check Out</span>
                  <span className="checkin-time-value">{todayStatus.checkOut || '--:--'}</span>
                </div>
              </div>

              {/* Duration Progress Bar */}
              <div className="duration-tracker">
                <div className="duration-bar-container">
                  <div
                    className="duration-bar-fill"
                    style={{
                      width: `${progressPct}%`,
                      background: progressPct >= 100 ? '#10b981' : progressPct >= 50 ? '#3b82f6' : '#f59e0b',
                    }}
                  />
                </div>
                <div className="duration-info">
                  <span className="duration-worked">{fmtDur(liveWorked)}</span>
                  <span className="duration-separator">/</span>
                  <span className="duration-required">8h 0m</span>
                  {todayStatus.lunchDuration > 0 && (
                    <span className="duration-lunch">(lunch: {todayStatus.lunchDuration}m)</span>
                  )}
                  {isOnLunch && <span className="on-lunch-badge">On Lunch</span>}
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                <span className="attendance-status-badge" style={{ background: STATUS_COLORS[todayStatus.status] }}>
                  {todayStatus.status}
                </span>
                <span className="attendance-approval-badge" style={{ background: APPROVAL_COLORS[todayStatus.approval] }}>
                  {todayStatus.approval}
                </span>
              </div>
            </div>
          ) : (
            <p style={{ color: '#6b7280' }}>Not checked in today</p>
          )}
        </div>

        <div className="checkin-panel-right">
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
            {!todayStatus ? (
              <button className="btn-checkin" onClick={handleCheckIn} disabled={checkingIn}>
                <FiCheck /> {checkingIn ? 'Checking in...' : 'Check In'}
              </button>
            ) : !todayStatus.checkOut ? (
              <>
                {/* Lunch button */}
                {!todayStatus.lunchStart ? (
                  <button className="btn-lunch" onClick={handleLunchStart} disabled={lunchLoading}>
                    <FiCoffee /> Start Lunch
                  </button>
                ) : !todayStatus.lunchEnd ? (
                  <button className="btn-lunch-end" onClick={handleLunchEnd} disabled={lunchLoading}>
                    <FiCoffee /> End Lunch
                  </button>
                ) : (
                  <span className="lunch-done-text"><FiCoffee /> Lunch: {todayStatus.lunchDuration}m</span>
                )}
                <button className="btn-checkout" onClick={handleCheckOut} disabled={checkingOut}>
                  <FiX /> {checkingOut ? 'Checking out...' : 'Check Out'}
                </button>
              </>
            ) : (
              <span className="checkin-done-text">Day complete</span>
            )}
          </div>
        </div>
      </div>}

      {/* Filters */}
      <div className="filters-row">
        <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} title="From date"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }} />
        <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} title="To date"
          style={{ padding: '0.5rem 0.75rem', border: '1px solid #d1d5db', borderRadius: '8px', fontSize: '0.9rem' }} />
        {(startDate || endDate) && (
          <button className="btn-secondary" style={{ padding: '0.5rem 0.75rem' }}
            onClick={() => { setStartDate(''); setEndDate(''); }}>Clear</button>
        )}
        {!startDate && !endDate && <>
          <select value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => <option key={i + 1} value={i + 1}>{new Date(2000, i).toLocaleString('default', { month: 'long' })}</option>)}
          </select>
          <select value={year} onChange={e => setYear(Number(e.target.value))}>
            {[2024, 2025, 2026].map(y => <option key={y} value={y}>{y}</option>)}
          </select>
        </>}
        {isAdminHR && (
          <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)}>
            <option value="">All Employees</option>
            {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
          </select>
        )}
        {isAdminHR && (
          <select value={filterApproval} onChange={e => setFilterApproval(e.target.value)}>
            <option value="">All Approvals</option>
            <option value="Pending">Pending</option>
            <option value="Approved">Approved</option>
            <option value="Rejected">Rejected</option>
          </select>
        )}
        {isAdminHR && pendingCount > 0 && (
          <span className="pending-count-badge">{pendingCount} pending</span>
        )}
      </div>

      {/* Bulk Actions */}
      {isAdminHR && selectedIds.length > 0 && (
        <div className="bulk-actions-bar">
          <span>{selectedIds.length} selected</span>
          <button className="btn-sm btn-success" onClick={() => handleBulkApproval('Approved')}><FiCheck /> Approve All</button>
          <button className="btn-sm btn-danger" onClick={() => handleBulkApproval('Rejected')}><FiX /> Reject All</button>
        </div>
      )}

      {/* HR Mark Attendance */}
      {showMark && isAdminHR && (
        <div className="form-card" style={{ marginBottom: '1rem' }}>
          <h3>Mark Attendance (Auto-approved)</h3>
          <form onSubmit={handleMark}>
            <div className="form-grid">
              <div className="form-group"><label>Employee</label>
                <select value={markForm.employee} onChange={e => setMarkForm({ ...markForm, employee: e.target.value })} required>
                  <option value="">Select Employee</option>
                  {employees.map(emp => <option key={emp._id} value={emp._id}>{emp.name}</option>)}
                </select>
              </div>
              <div className="form-group"><label>Date</label><input type="date" value={markForm.date} onChange={e => setMarkForm({ ...markForm, date: e.target.value })} required /></div>
              <div className="form-group"><label>Status</label>
                <select value={markForm.status} onChange={e => setMarkForm({ ...markForm, status: e.target.value })}>
                  <option>Present</option><option>Absent</option><option>Half Day</option><option>Late</option>
                </select>
              </div>
              <div className="form-group"><label>Check In</label><input type="time" value={markForm.checkIn} onChange={e => setMarkForm({ ...markForm, checkIn: e.target.value })} /></div>
              <div className="form-group"><label>Check Out</label><input type="time" value={markForm.checkOut} onChange={e => setMarkForm({ ...markForm, checkOut: e.target.value })} /></div>
            </div>
            <button type="submit" className="btn-primary" style={{ marginTop: '1rem' }}>Submit</button>
          </form>
        </div>
      )}

      {/* Attendance Table */}
      {loading ? <div className="loading">Loading...</div> : (
        <div className="form-card">
          <table className="table">
            <thead>
              <tr>
                {isAdminHR && (
                  <th style={{ width: 40 }}>
                    <input type="checkbox" onChange={toggleSelectAll} checked={selectedIds.length > 0 && selectedIds.length === attendance.filter(a => a.approval === 'Pending').length} />
                  </th>
                )}
                <th>Sr. No.</th>
                <th>Employee</th>
                <th>Date</th>
                <th>Status</th>
                <th>Check In</th>
                <th>Location</th>
                <th>Lunch</th>
                <th>Check Out</th>
                <th>Worked</th>
                <th>Extra Hrs</th>
                <th>Approval</th>
                {isAdminHR && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {attendance.map((a, index) => {
                const worked = a.totalWorked || 0;
                const pct = Math.min(100, Math.round((worked / REQUIRED_MINUTES) * 100));
                return (
                  <tr key={a._id}>
                    {isAdminHR && (
                      <td>
                        {a.approval === 'Pending' && (
                          <input type="checkbox" checked={selectedIds.includes(a._id)} onChange={() => toggleSelect(a._id)} />
                        )}
                      </td>
                    )}
                    <td>{index + 1}</td>
                    <td>{a.employee?.name || 'N/A'}</td>
                    <td>{new Date(a.date).toLocaleDateString()}</td>
                    <td>
                      <span style={{ color: STATUS_COLORS[a.status] || '#333', fontWeight: 600 }}>{a.status}</span>
                    </td>
                    <td>{a.checkIn || '-'}</td>
                    <td>
                      {a.checkInLocation?.lat ? (
                        <a
                          href={`https://www.google.com/maps?q=${a.checkInLocation.lat},${a.checkInLocation.lng}`}
                          target="_blank"
                          rel="noreferrer"
                          style={{ fontSize: '0.8rem', color: '#4f46e5' }}
                        >
                          📍 {a.checkInLocation.lat.toFixed(4)}, {a.checkInLocation.lng.toFixed(4)}
                        </a>
                      ) : <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>}
                    </td>
                    <td>
                      {a.lunchStart ? (
                        <span style={{ fontSize: '0.8rem' }}>
                          {a.lunchStart} - {a.lunchEnd || '...'} <span style={{ color: '#6b7280' }}>({a.lunchDuration || 0}m)</span>
                        </span>
                      ) : (
                        <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>30m default</span>
                      )}
                    </td>
                    <td>{a.checkOut || '-'}</td>
                    <td>
                      {a.checkOut ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                          <div className="table-dur-bar">
                            <div className="table-dur-fill" style={{ width: `${pct}%`, background: pct >= 100 ? '#10b981' : pct >= 50 ? '#3b82f6' : '#f59e0b' }} />
                          </div>
                          <span style={{ fontSize: '0.75rem', fontWeight: 600, color: pct >= 100 ? '#10b981' : '#374151' }}>{fmtDur(worked)}</span>
                        </div>
                      ) : '-'}
                    </td>
                    <td>
                      {a.extraHoursStatus && a.extraHoursStatus !== 'None' ? (
                        <div>
                          <span style={{
                            fontSize: '0.75rem', fontWeight: 600,
                            color: a.extraHoursStatus === 'Approved' ? '#10b981' : a.extraHoursStatus === 'Rejected' ? '#ef4444' : '#f59e0b'
                          }}>
                            {fmtDur(a.extraHours)} · {a.extraHoursStatus}
                          </span>
                          {a.extraHoursNote && <div style={{ fontSize: '0.65rem', color: '#6b7280' }}>{a.extraHoursNote}</div>}
                        </div>
                      ) : (
                        !isAdminHR && a.checkOut && a.extraHoursStatus === 'None' ? (
                          <button className="btn-sm" style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem', background: '#ede9fe', color: '#7c3aed', border: 'none', borderRadius: 6, cursor: 'pointer' }}
                            onClick={() => { setExtraModal({ id: a._id }); setExtraForm({ hours: '', minutes: '', note: '' }); }}
                          ><FiZap style={{ marginRight: 2 }} /> Request</button>
                        ) : <span style={{ color: '#9ca3af', fontSize: '0.8rem' }}>—</span>
                      )}
                    </td>
                    <td>
                      <span className="attendance-approval-badge" style={{ background: APPROVAL_COLORS[a.approval] }}>
                        {a.approval}
                      </span>
                      {a.approvedBy && (
                        <span style={{ fontSize: '0.65rem', color: '#6b7280', display: 'block' }}>
                          by {a.approvedBy.name}
                        </span>
                      )}
                    </td>
                    {isAdminHR && (
                      <td>
                        <div className="actions">
                          {a.approval === 'Pending' && (
                            <>
                              <button className="btn-sm btn-success" onClick={() => handleApproval(a._id, 'Approved')} title="Approve"><FiCheck /></button>
                              <button className="btn-sm btn-danger" onClick={() => handleApproval(a._id, 'Rejected')} title="Reject"><FiX /></button>
                            </>
                          )}
                          {a.status === 'Late' && (
                            <button className="btn-sm btn-success" onClick={() => handleMarkPresent(a._id)} title="Mark as Present"><FiCheck /></button>
                          )}
                          {a.extraHoursStatus === 'Pending' && (
                            <>
                              <button className="btn-sm btn-success" onClick={() => handleExtraAction(a._id, 'approve')} title="Approve Extra Hours"><FiZap /></button>
                              <button className="btn-sm btn-danger" onClick={() => handleExtraAction(a._id, 'reject')} title="Reject Extra Hours"><FiX /></button>
                            </>
                          )}
                          <button className="btn-sm btn-danger" onClick={() => handleDelete(a._id)} title="Delete Record"><FiTrash2 /></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
              {attendance.length === 0 && (
                <tr><td colSpan={isAdminHR ? 12 : 11} style={{ textAlign: 'center' }}>No attendance records</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Extra Hours Request Modal */}
      {extraModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="form-card" style={{ width: '100%', maxWidth: 420, margin: '1rem' }}>
            <h3><FiZap style={{ marginRight: '0.4rem', color: '#7c3aed' }} /> Request Extra Hours</h3>
            <form onSubmit={handleRequestExtra}>
              <div className="form-grid" style={{ gridTemplateColumns: '1fr 1fr' }}>
                <div className="form-group">
                  <label>Hours</label>
                  <input type="number" min="0" max="12" value={extraForm.hours} onChange={e => setExtraForm({ ...extraForm, hours: e.target.value })} placeholder="0" />
                </div>
                <div className="form-group">
                  <label>Minutes</label>
                  <input type="number" min="0" max="59" value={extraForm.minutes} onChange={e => setExtraForm({ ...extraForm, minutes: e.target.value })} placeholder="0" />
                </div>
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label>Note / Reason</label>
                  <textarea value={extraForm.note} onChange={e => setExtraForm({ ...extraForm, note: e.target.value })} rows={2} placeholder="What did you work on?" />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                <button type="submit" className="btn-primary">Submit Request</button>
                <button type="button" className="btn-secondary" onClick={() => setExtraModal(null)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendancePage;
